/**
 * Penalty shootout logic.
 *
 * Each player gets 5 hidden attack cards + 5 hidden defense cards
 * drawn from the cards REMAINING in the shared deck at match end.
 * Cards are hidden from both players (the only randomness is the source order).
 *
 * Format: best of 5. If still tied → sudden death (one shot each per round).
 */

import type {
  Action,
  Card,
  GameEvent,
  GameState,
  PenaltyShot,
  PenaltyState,
  PlayerId,
  Rank,
  Suit,
} from './types';
import { compareCards } from './rules';
import { mulberry32, shuffleInPlace } from './rng';

/**
 * Phase 3.8.2.4 — Jokers must NEVER appear in penalty batches.
 * Predicate isolates a single canonical check for readability + reuse.
 */
function isJoker(c: Card): boolean {
  return c.suit === 'joker' || c.rank === 'JOKER';
}

/**
 * Phase 3.8.2.4 — Deterministic synthesis of a fresh non-Joker card pool
 * used ONLY as a last-resort fallback when the natural deck + discard have
 * been depleted (e.g. very long sudden-death series). The pool is built from
 * the standard 52-card ranks × 4 suits, seeded from the match seed + batch
 * index so that both clients (and the server sidecar) produce the exact same
 * sequence — no divergence, no Joker leakage.
 */
const NON_JOKER_RANKS: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const NON_JOKER_SUITS: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];
const RANK_VALUE: Record<string, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10,
  'J': 11, 'Q': 12, 'K': 13, 'A': 14,
};

function synthNonJokerPool(state: GameState, batchIndex: number, salt: number): Card[] {
  const seed = ((state.seed | 0) ^ (state.tick | 0) ^ ((batchIndex + 1) * 0x9E3779B1) ^ (salt * 0x85EBCA77)) >>> 0;
  const rng = mulberry32(seed);
  const pool: Card[] = [];
  for (const suit of NON_JOKER_SUITS) {
    for (const rank of NON_JOKER_RANKS) {
      pool.push({
        id: `pen-syn-${batchIndex}-${salt}-${suit}-${rank}`,
        suit,
        rank,
        value: RANK_VALUE[rank as string],
      });
    }
  }
  shuffleInPlace(pool, rng);
  return pool;
}

/**
 * Phase 3.8.2 — Draw a fresh penalty batch (5 attacks + 5 defenses per side)
 * from the SHOULD-be-remaining pool. Used both for the initial deal AND for
 * every sudden-death rollover so series 6-10 / 11-15 / etc. get NEW cards
 * (bug fix: previously we recycled the same 5 hands, producing duplicates).
 *
 * `state` is mutated: pool cards consumed from deck (then discard as a
 * fallback) so that identity of drawn cards is deterministic given the
 * current state.
 */
function dealPenaltyBatch(state: GameState, batchIndex: number = 1): {
  p1Attacks: Card[]; p1Defenses: Card[]; p2Attacks: Card[]; p2Defenses: Card[];
} {
  const need = 20;
  const useful: Card[] = [];

  // Phase 3.8.2.4 — Jokers are STRICTLY forbidden in penalty batches.
  // Only take non-Joker cards from deck and discard. Any Joker encountered
  // is skipped in place (remains where it is so normal gameplay behavior
  // stays unchanged if the state is ever re-used).
  const takeNonJokerFrom = (pool: Card[]) => {
    let i = 0;
    while (i < pool.length && useful.length < need) {
      const c = pool[i];
      if (!isJoker(c)) {
        useful.push(c);
        pool.splice(i, 1);
        continue;
      }
      i += 1;
    }
  };

  takeNonJokerFrom(state.deck);
  takeNonJokerFrom(state.discard);

  // Phase 3.8.2.4 — Fallback: deterministically synthesize additional
  // non-Joker cards so the shootout can NEVER deadlock and NEVER leak a
  // Joker. Deterministic seeding (state.seed + tick + batchIndex) keeps
  // both clients + the server sidecar perfectly in sync for online play.
  let salt = 0;
  while (useful.length < need) {
    const pool = synthNonJokerPool(state, batchIndex, salt);
    for (const c of pool) {
      if (useful.length >= need) break;
      useful.push(c);
    }
    salt += 1;
  }

  const p1Attacks: Card[] = [];
  const p1Defenses: Card[] = [];
  const p2Attacks: Card[] = [];
  const p2Defenses: Card[] = [];
  for (let k = 0; k < 5; k++) {
    p1Attacks.push(useful[k]);
    p1Defenses.push(useful[5 + k]);
    p2Attacks.push(useful[10 + k]);
    p2Defenses.push(useful[15 + k]);
  }
  return { p1Attacks, p1Defenses, p2Attacks, p2Defenses };
}

/**
 * Build the penalty state from whatever cards remain (deck + discard if needed).
 * The cards are NOT comparable to playing card semantics: they keep their face,
 * but we draw the next 20 unique cards from the combined pool.
 */
export function buildPenaltyStateFromRemaining(state: GameState): PenaltyState {
  const deal = dealPenaltyBatch(state, 1);

  return {
    hands: {
      p1: { attacks: deal.p1Attacks, defenses: deal.p1Defenses },
      p2: { attacks: deal.p2Attacks, defenses: deal.p2Defenses },
    },
    used: {
      p1: { attacks: [], defenses: [] },
      p2: { attacks: [], defenses: [] },
    },
    shots: [],
    scores: { p1: 0, p2: 0 },
    currentShooter: 'p1',
    round: 1,
    suddenDeath: false,
    batchIndex: 1,
    finished: false,
    selection: { attackIndex: null, defenseIndex: null },
    locks: {
      shooter: { index: null, locked: false },
      defender: { index: null, locked: false },
    },
  };
}

function otherPlayer(p: PlayerId): PlayerId {
  return p === 'p1' ? 'p2' : 'p1';
}

/**
 * Inspect both scores and decide if penalty is decided.
 * Rules:
 *  - Best of 5: after round 5 (5 shots each), higher score wins.
 *  - Early termination: if remaining shots can't change outcome, finish.
 *  - Sudden death: after round 5, one shot each; if tied, continue.
 */
function maybeFinalize(pen: PenaltyState, events: GameEvent[], state: GameState) {
  const p1Shot = pen.shots.filter((s) => s.shooter === 'p1').length;
  const p2Shot = pen.shots.filter((s) => s.shooter === 'p2').length;

  // Best-of-5 phase
  if (!pen.suddenDeath) {
    // After both players have shot 5 times, decide.
    if (p1Shot >= 5 && p2Shot >= 5) {
      if (pen.scores.p1 !== pen.scores.p2) {
        pen.finished = true;
        pen.winner = pen.scores.p1 > pen.scores.p2 ? 'p1' : 'p2';
      } else {
        // Phase 3.8.2 — Sudden death starts with a BRAND NEW deal (bug fix:
        // previously we reset the `used` arrays but kept the same 5 hands,
        // causing series 6-10 to replay the exact same cards as 1-5).
        pen.suddenDeath = true;
        rolloverPenaltyBatch(pen, state, events);
      }
      return;
    }
    // Early termination — current lead is unreachable.
    const p1Remaining = 5 - p1Shot;
    const p2Remaining = 5 - p2Shot;
    if (pen.scores.p1 > pen.scores.p2 + p2Remaining) {
      pen.finished = true;
      pen.winner = 'p1';
      return;
    }
    if (pen.scores.p2 > pen.scores.p1 + p1Remaining) {
      pen.finished = true;
      pen.winner = 'p2';
      return;
    }
    return;
  }

  // Sudden death — after BOTH players have shot in this round (equal counts), if scores differ, finish.
  if (p1Shot === p2Shot && pen.scores.p1 !== pen.scores.p2) {
    pen.finished = true;
    pen.winner = pen.scores.p1 > pen.scores.p2 ? 'p1' : 'p2';
  }
}

/**
 * Deal a fresh sudden-death batch of 5 attacks + 5 defenses per player. Called
 * (a) when best-of-5 ends in a tie, and (b) whenever a sudden-death batch is
 * exhausted (both players have used all 5 of their new attacks). Resets
 * `used` arrays and increments `batchIndex` for telemetry / UI.
 */
function rolloverPenaltyBatch(pen: PenaltyState, state: GameState, events: GameEvent[]) {
  const nextIndex = (pen.batchIndex || 1) + 1;
  const deal = dealPenaltyBatch(state, nextIndex);
  pen.hands = {
    p1: { attacks: deal.p1Attacks, defenses: deal.p1Defenses },
    p2: { attacks: deal.p2Attacks, defenses: deal.p2Defenses },
  };
  pen.used = {
    p1: { attacks: [], defenses: [] },
    p2: { attacks: [], defenses: [] },
  };
  pen.selection = { attackIndex: null, defenseIndex: null };
  pen.locks = {
    shooter: { index: null, locked: false },
    defender: { index: null, locked: false },
  };
  pen.batchIndex = nextIndex;
  events.push({ type: 'PENALTY_BATCH_DEALT', batchIndex: pen.batchIndex });
}

/**
 * PHASE 4.3.1A — internal helper. Ensures `pen.locks` is present on
 * legacy states that were serialised before the simultaneous-lock
 * model shipped. Called at the top of `applyPenaltyAction` so every
 * reducer path can assume `pen.locks` is defined and idempotently
 * mirrors any legacy `pen.selection.*` values that were already set.
 */
function ensureLocks(pen: PenaltyState) {
  if (!pen.locks) {
    pen.locks = {
      shooter: {
        index: pen.selection?.attackIndex ?? null,
        locked: pen.selection?.attackIndex !== null && pen.selection?.attackIndex !== undefined,
      },
      defender: {
        index: pen.selection?.defenseIndex ?? null,
        locked: pen.selection?.defenseIndex !== null && pen.selection?.defenseIndex !== undefined,
      },
    };
  }
}

/**
 * PHASE 4.3.1A — atomic reveal + resolution. Runs exactly once per
 * penalty decision, when BOTH the shooter and defender have locked
 * their private card. Existing GOAL/SAVE mathematics (`compareCards`
 * result → 'goal' or 'save', +1 score iff goal), existing card
 * lifecycle (`used`), and existing shot-order + sudden-death rollover
 * are all preserved verbatim from the pre-4.3.1A implementation.
 */
function resolvePenaltyReveal(state: GameState, events: GameEvent[]) {
  const pen = state.penalty!;
  if (pen.finished) return;
  ensureLocks(pen);
  const shooter = pen.currentShooter;
  const defender = otherPlayer(shooter);
  const sLock = pen.locks!.shooter;
  const dLock = pen.locks!.defender;
  if (!sLock.locked || !dLock.locked) return;                // not both locked yet
  if (sLock.index === null || dLock.index === null) return;  // defensive

  const aIdx = sLock.index;
  const dIdx = dLock.index;
  const attackCard = pen.hands[shooter].attacks[aIdx];
  const defenseCard = pen.hands[defender].defenses[dIdx];

  // PHASE 5.0B-4.2B — HARD INTEGRITY BOUNDARY (last line of defence).
  // Even if PENALTY_PICK_* somehow bypassed the earlier guard (e.g.
  // legacy client, unit test injecting locks directly), a shot must
  // NEVER be created from a Joker / Double Chance card. Reset the
  // offending lock so the timeout-auto path (which already filters
  // Jokers) can re-resolve with a legal card.
  if (isJoker(attackCard)) {
    pen.locks!.shooter = { index: null, locked: false };
    pen.selection.attackIndex = null;
    return;
  }
  if (isJoker(defenseCard)) {
    pen.locks!.defender = { index: null, locked: false };
    pen.selection.defenseIndex = null;
    return;
  }

  pen.used[shooter].attacks.push(aIdx);
  pen.used[defender].defenses.push(dIdx);

  const cmp = compareCards(attackCard, defenseCard);
  const outcome: 'goal' | 'save' = cmp > 0 ? 'goal' : 'save';
  if (cmp > 0) pen.scores[shooter] += 1;

  const shot: PenaltyShot = { shooter, attackCard, defenseCard, outcome };
  pen.shots.push(shot);
  events.push({ type: 'PENALTY_REVEAL', shot });

  // Reset lock + selection, swap shooter (existing order rule).
  pen.selection = { attackIndex: null, defenseIndex: null };
  pen.locks = {
    shooter: { index: null, locked: false },
    defender: { index: null, locked: false },
  };
  pen.currentShooter = defender;
  const p1ShotCount = pen.shots.filter((s) => s.shooter === 'p1').length;
  const p2ShotCount = pen.shots.filter((s) => s.shooter === 'p2').length;
  pen.round = Math.max(p1ShotCount, p2ShotCount) + 1;
  maybeFinalize(pen, events, state);

  // Sudden-death batch rollover — unchanged.
  if (pen.suddenDeath && !pen.finished) {
    if (
      pen.used.p1.attacks.length >= 5
      && pen.used.p2.attacks.length >= 5
    ) {
      rolloverPenaltyBatch(pen, state, events);
    }
  }

  if (pen.finished && pen.winner) {
    state.phase = 'finished';
    state.endState = { winner: pen.winner, reason: 'penalty' };
    events.push({ type: 'MATCH_END', winner: pen.winner });
  }
}

/**
 * Apply a penalty-phase action.
 *
 * PHASE 4.3.1A — Simultaneous strategic lock model.
 *   • `PENALTY_PICK_ATTACK` is the SHOOTER's lock. Idempotent — once
 *     the shooter has locked, subsequent PICK_ATTACK actions are
 *     ignored (they cannot change the card).
 *   • `PENALTY_PICK_DEFENSE` is the DEFENDER's lock. Idempotent.
 *   • Reveal fires automatically as soon as BOTH sides are locked —
 *     no separate CONFIRM needed. `PENALTY_CONFIRM` is retained as a
 *     no-op for wire-protocol compatibility.
 *
 * The engine does NOT enforce sender identity — that is the WS
 * boundary's responsibility. The engine simply routes PICK_ATTACK to
 * the shooter slot and PICK_DEFENSE to the defender slot; the server
 * validates that the sender actually IS that role before dispatching.
 */
export function applyPenaltyAction(state: GameState, action: Action, events: GameEvent[]) {
  const pen = state.penalty!;
  if (pen.finished) return;
  ensureLocks(pen);
  const shooter = pen.currentShooter;
  const defender = otherPlayer(shooter);

  if (action.type === 'PENALTY_PICK_ATTACK') {
    // Idempotent — cannot change once locked.
    if (pen.locks!.shooter.locked) return;
    // Must be a currently legal card for the shooter.
    if (pen.used[shooter].attacks.includes(action.index)) return;
    if (action.index < 0 || action.index >= (pen.hands[shooter].attacks?.length || 0)) return;
    // PHASE 5.0B-4.2B — HARD INTEGRITY BOUNDARY: penalty resolution
    // must NEVER accept a Joker / Double Chance card. If a Joker
    // somehow leaked into `pen.hands` (legacy state, hydration bug),
    // reject the action entirely — human tap AND bot pick.
    const candidateAtk = pen.hands[shooter].attacks[action.index];
    if (isJoker(candidateAtk)) return;
    pen.locks!.shooter = { index: action.index, locked: true };
    pen.selection.attackIndex = action.index;      // mirror for legacy readers
    resolvePenaltyReveal(state, events);
    return;
  }

  if (action.type === 'PENALTY_PICK_DEFENSE') {
    // Idempotent — cannot change once locked.
    if (pen.locks!.defender.locked) return;
    if (pen.used[defender].defenses.includes(action.index)) return;
    if (action.index < 0 || action.index >= (pen.hands[defender].defenses?.length || 0)) return;
    // PHASE 5.0B-4.2B — HARD INTEGRITY BOUNDARY (see PENALTY_PICK_ATTACK).
    const candidateDef = pen.hands[defender].defenses[action.index];
    if (isJoker(candidateDef)) return;
    pen.locks!.defender = { index: action.index, locked: true };
    pen.selection.defenseIndex = action.index;     // mirror for legacy readers
    resolvePenaltyReveal(state, events);
    return;
  }

  if (action.type === 'PENALTY_CONFIRM') {
    // PHASE 4.3.1A — reveal is now automatic. CONFIRM is retained as
    // a no-op for wire compatibility so pre-existing clients (and the
    // bot's third dispatch) can't break the state machine. If both
    // sides happen to be locked, this call is idempotent.
    resolvePenaltyReveal(state, events);
    return;
  }
}
